import { PERSONAL_ACCOUNT_GOVERNANCE_NOTE } from "@/lib/personal-account-governance";

export type PolicyAction = "flag" | "block" | "warn" | "quarantine";

export type PolicyMessageType =
  | "user_message"
  | "tool_request"
  | "tool_response"
  | "assistant_message";

export type RuleCategory =
  | "secrets"
  | "financial"
  | "pii"
  | "government_ids"
  | "healthcare"
  | "prompt_policy"
  | "prompt_injection"
  | "off_policy"
  | "shadow_mcp"
  | "destructive_tool"
  | "cli_destructive"
  | "account_identity"
  | "custom";

export type DetectionRule = {
  id: string;
  title: string;
  source:
    | "gitleaks"
    | "presidio"
    | "prompt_injection"
    | "llm_judge"
    | "account_identity";
  // Optional per-rule explanation shown under the title in the policy form's
  // Customize sheet. Use when the title alone doesn't make clear what
  // enabling the rule flags (most rules are self-describing and omit it).
  description?: string;
  // When true the rule is kept in the catalog so legacy risk_results still
  // resolve their human-readable title via risk-utils.ts, but it is hidden
  // from the policy create/edit form so users cannot select it on new
  // policies. Use for rules that are accepted-but-deprecated or whose
  // upstream scanner is unreliable.
  hidden?: boolean;
};

/** Which engine backs the personal-data categories in the policy editor.
 *
 *  - `presidio`: the legacy per-entity scanner. Personal data is split into
 *    four categories (financial, pii, government_ids, healthcare), each with a
 *    rule list the user can narrow, plus a confidence threshold.
 *  - `llm`: the fine-tuned risk analyzer replaces the legacy engine for the
 *    organization. The model decides which personal-data category applies per
 *    finding, so the editor collapses the four categories into a single
 *    category-level `pii` detector with no rule list, no sensitivity and no
 *    entity-type exclusions.
 *
 *  The mode follows the multivariate PostHog flag `gram-risk-llm-analyzer`
 *  (see `useDetectorMode`): `off` and `shadow` render `presidio` (under
 *  `shadow` the analyzer also scans server-side for comparison but never
 *  enforces, so the editor keeps the legacy controls); only `llm` renders
 *  `llm`. Until the flag is converted to multivariate in PostHog it has no
 *  variant, and its boolean-enabled read also renders `llm` (the transition
 *  rule in `useDetectorMode`). */
export type DetectorMode = "presidio" | "llm";

export type RuleCategoryMeta = {
  label: string;
  description: string;
  icon: string;
};

export const RULE_CATEGORY_META: Record<RuleCategory, RuleCategoryMeta> = {
  secrets: {
    label: "Secrets",
    description: "API keys, tokens, private keys, credentials",
    icon: "key-round",
  },
  financial: {
    label: "Financial Information",
    description: "Credit cards, bank accounts, routing numbers, IBAN codes",
    icon: "credit-card",
  },
  pii: {
    label: "Personal Identifiable Information",
    description: "Phone numbers, email addresses, IP and MAC addresses",
    icon: "user",
  },
  government_ids: {
    label: "Government Identifiers",
    description: "SSNs, passport numbers, national IDs, tax IDs",
    icon: "landmark",
  },
  healthcare: {
    label: "Healthcare Information",
    description: "Medical record numbers, patient data, Medicare IDs",
    icon: "heart-pulse",
  },
  prompt_policy: {
    label: "Prompt Policies",
    description: "Natural-language guardrails evaluated by the policy judge",
    icon: "message-square-warning",
  },
  prompt_injection: {
    label: "Prompt Injection",
    description:
      "Jailbreaks, instruction overrides, and prompt extraction — in a user prompt or smuggled through tool outputs. Evaluated semantically by an LLM judge.",
    icon: "syringe",
  },
  off_policy: {
    label: "Off-Policy Content",
    description:
      "Requests that violate usage policies or acceptable use guidelines",
    icon: "ban",
  },
  // One Shadow AI bucket. Today its findings are tool calls to MCP servers no
  // Speakeasy toolset manages; the AI tools the device agent finds join it
  // once their detections become findings.
  shadow_mcp: {
    label: "Shadow AI",
    description:
      "Tool calls to MCP servers that no Speakeasy toolset manages. Requires Speakeasy hooks to be installed on the agent.",
    icon: "shield-off",
  },
  destructive_tool: {
    label: "Destructive Tools",
    description:
      "MCP tool calls whose platform tool definition is annotated as destructive. Requires Speakeasy hooks and platform-issued MCP tool metadata.",
    icon: "shield-alert",
  },
  cli_destructive: {
    label: "Destructive CLI Commands",
    description:
      "Tool calls whose arguments match a curated set of destructive shell, git, database, or cloud CLI patterns (rm -rf, git push --force, DROP TABLE, kubectl delete ns, ...). Applies to native Bash / run_terminal_cmd as well as MCP-routed tools whose arguments carry destructive content.",
    icon: "terminal",
  },
  account_identity: {
    label: "Non-Corporate Accounts",
    description:
      "Sessions authenticated with a personal AI account or an email domain outside the approved list. Uses the account attribution captured by session ingest.",
    icon: "user-x",
  },
  custom: {
    label: "Custom Rules",
    description:
      "Organization-defined detection rules over message content, tool calls, and arguments",
    icon: "regex",
  },
};

/** Copy that changes with the detector mode. Under the LLM analyzer the
 *  `pii` card stands for every kind of personal data, so its Presidio-specific
 *  entity list no longer describes it. */
const LLM_RULE_CATEGORY_META: Partial<Record<RuleCategory, RuleCategoryMeta>> =
  {
    pii: {
      label: "PII",
      description:
        "Personal data about an identifiable person; the category is decided per finding.",
      icon: "user",
    },
  };

/**
 * Display name for a category key from the API, or the key itself when this
 * build doesn't know it.
 */
export function ruleCategoryLabel(category: string): string {
  return RULE_CATEGORY_META[category as RuleCategory]?.label ?? category;
}

/** Label, description and icon for a category under `mode`. Prefer this over
 *  indexing `RULE_CATEGORY_META` directly anywhere the policy editor renders a
 *  category, so the LLM-analyzer copy applies without mutating the constant. */
export function ruleCategoryMeta(
  category: RuleCategory,
  mode: DetectorMode = "presidio",
): RuleCategoryMeta {
  if (mode === "llm") {
    return LLM_RULE_CATEGORY_META[category] ?? RULE_CATEGORY_META[category];
  }
  return RULE_CATEGORY_META[category];
}

// Display order for the message kinds a policy can scope to.
export const ALL_POLICY_MESSAGE_TYPES: PolicyMessageType[] = [
  "user_message",
  "tool_request",
  "tool_response",
  "assistant_message",
];

export const POLICY_MESSAGE_TYPE_META: Record<
  PolicyMessageType,
  { label: string; description: string }
> = {
  user_message: {
    label: "User Prompts",
    description: "Prompt text submitted by the user before the agent acts",
  },
  tool_request: {
    label: "Tool Requests",
    description: "Arguments and payloads sent into tool calls",
  },
  tool_response: {
    label: "Tool Responses",
    description: "Data returned from tool executions",
  },
  assistant_message: {
    label: "Assistant Messages",
    description: "Assistant text responses that are not tool-call requests",
  },
};

// Provider -> family for breaking the large `secrets` catalog into navigable
// sub-groups. Keyed by the token after `secret.` up to the first underscore
// (e.g. `secret.aws_access_token` -> "aws"). Unmapped/new providers fall into
// "Other", so the catalog keeps working as gitleaks rules are added upstream.
const SECRET_FAMILY_BY_PROVIDER: Record<string, string> = {
  // Cloud & Infrastructure
  aws: "Cloud & Infrastructure",
  azure: "Cloud & Infrastructure",
  gcp: "Cloud & Infrastructure",
  alibaba: "Cloud & Infrastructure",
  digitalocean: "Cloud & Infrastructure",
  heroku: "Cloud & Infrastructure",
  flyio: "Cloud & Infrastructure",
  scalingo: "Cloud & Infrastructure",
  netlify: "Cloud & Infrastructure",
  fastly: "Cloud & Infrastructure",
  cloudflare: "Cloud & Infrastructure",
  openshift: "Cloud & Infrastructure",
  kubernetes: "Cloud & Infrastructure",
  pulumi: "Cloud & Infrastructure",
  hashicorp: "Cloud & Infrastructure",
  vault: "Cloud & Infrastructure",
  yandex: "Cloud & Infrastructure",
  cisco: "Cloud & Infrastructure",
  // Source Control & Packages
  github: "Source Control & Packages",
  gitlab: "Source Control & Packages",
  bitbucket: "Source Control & Packages",
  sourcegraph: "Source Control & Packages",
  jfrog: "Source Control & Packages",
  artifactory: "Source Control & Packages",
  npm: "Source Control & Packages",
  nuget: "Source Control & Packages",
  pypi: "Source Control & Packages",
  rubygems: "Source Control & Packages",
  clojars: "Source Control & Packages",
  postman: "Source Control & Packages",
  // CI & DevOps
  codecov: "CI & DevOps",
  droneci: "CI & DevOps",
  travisci: "CI & DevOps",
  harness: "CI & DevOps",
  octopus: "CI & DevOps",
  infracost: "CI & DevOps",
  doppler: "CI & DevOps",
  launchdarkly: "CI & DevOps",
  sidekiq: "CI & DevOps",
  snyk: "CI & DevOps",
  sonar: "CI & DevOps",
  prefect: "CI & DevOps",
  rapidapi: "CI & DevOps",
  // Payments & Fintech
  stripe: "Payments & Fintech",
  plaid: "Payments & Fintech",
  square: "Payments & Fintech",
  coinbase: "Payments & Fintech",
  kraken: "Payments & Fintech",
  kucoin: "Payments & Fintech",
  bittrex: "Payments & Fintech",
  gocardless: "Payments & Fintech",
  flutterwave: "Payments & Fintech",
  finicity: "Payments & Fintech",
  finnhub: "Payments & Fintech",
  duffel: "Payments & Fintech",
  easypost: "Payments & Fintech",
  shippo: "Payments & Fintech",
  lob: "Payments & Fintech",
  freshbooks: "Payments & Fintech",
  // Communication
  slack: "Communication",
  discord: "Communication",
  telegram: "Communication",
  twilio: "Communication",
  messagebird: "Communication",
  sendbird: "Communication",
  mattermost: "Communication",
  intercom: "Communication",
  zendesk: "Communication",
  beamer: "Communication",
  gitter: "Communication",
  // Email & Marketing
  mailchimp: "Email & Marketing",
  mailgun: "Email & Marketing",
  sendgrid: "Email & Marketing",
  sendinblue: "Email & Marketing",
  hubspot: "Email & Marketing",
  typeform: "Email & Marketing",
  contentful: "Email & Marketing",
  freemius: "Email & Marketing",
  // AI & ML
  anthropic: "AI & ML",
  openai: "AI & ML",
  cohere: "AI & ML",
  huggingface: "AI & ML",
  perplexity: "AI & ML",
  privateai: "AI & ML",
  // Observability
  datadog: "Observability",
  sentry: "Observability",
  grafana: "Observability",
  sumologic: "Observability",
  dynatrace: "Observability",
  new: "Observability",
  // Data & Analytics
  clickhouse: "Data & Analytics",
  databricks: "Data & Analytics",
  looker: "Data & Analytics",
  confluent: "Data & Analytics",
  planetscale: "Data & Analytics",
  algolia: "Data & Analytics",
  mapbox: "Data & Analytics",
  maxmind: "Data & Analytics",
  // Identity & Auth
  "1password": "Identity & Auth",
  okta: "Identity & Auth",
  authress: "Identity & Auth",
  intra42: "Identity & Auth",
  microsoft: "Identity & Auth",
  adobe: "Identity & Auth",
  jwt: "Identity & Auth",
  pkcs12: "Identity & Auth",
  age: "Identity & Auth",
  private: "Identity & Auth",
  atlassian: "Identity & Auth",
  // Productivity & SaaS
  airtable: "Productivity & SaaS",
  asana: "Productivity & SaaS",
  linear: "Productivity & SaaS",
  notion: "Productivity & SaaS",
  dropbox: "Productivity & SaaS",
  frameio: "Productivity & SaaS",
  readme: "Productivity & SaaS",
  shopify: "Productivity & SaaS",
  squarespace: "Productivity & SaaS",
  etsy: "Productivity & SaaS",
  twitter: "Productivity & SaaS",
  twitch: "Productivity & SaaS",
  facebook: "Productivity & SaaS",
  linkedin: "Productivity & SaaS",
  flickr: "Productivity & SaaS",
  nytimes: "Productivity & SaaS",
};

const SECRET_FAMILY_OTHER = "Other";

// Render order for rule families; "Other" always sorts last.
export const RULE_FAMILY_ORDER: string[] = [
  "Cloud & Infrastructure",
  "Source Control & Packages",
  "CI & DevOps",
  "Payments & Fintech",
  "Communication",
  "Email & Marketing",
  "AI & ML",
  "Observability",
  "Data & Analytics",
  "Identity & Auth",
  "Productivity & SaaS",
  SECRET_FAMILY_OTHER,
];

function secretRuleFamily(rule: DetectionRule): string {
  const provider = rule.id.replace(/^secret\./, "").split("_")[0] ?? "";
  return SECRET_FAMILY_BY_PROVIDER[provider] ?? SECRET_FAMILY_OTHER;
}

// Categories large enough to be unnavigable as a flat list get a family
// classifier; everything else renders flat. Currently just `secrets` (~200
// rules); other categories are small.
export const RULE_FAMILY_OF: Partial<
  Record<RuleCategory, (rule: DetectionRule) => string>
> = {
  secrets: secretRuleFamily,
};

// All available detection rules, organized by category
export const DETECTION_RULES: Record<RuleCategory, DetectionRule[]> = {
  secrets: [
    {
      id: "secret.1password_secret_key",
      title: "1Password Secret Key",
      source: "gitleaks",
    },
    {
      id: "secret.1password_service_account_token",
      title: "1Password Service Account Token",
      source: "gitleaks",
    },
    {
      id: "secret.adafruit_api_key",
      title: "Adafruit API Key",
      source: "gitleaks",
    },
    {
      id: "secret.adobe_client_id",
      title: "Adobe Client ID",
      source: "gitleaks",
    },
    {
      id: "secret.adobe_client_secret",
      title: "Adobe Client Secret",
      source: "gitleaks",
    },
    {
      id: "secret.age_secret_key",
      title: "Age Secret Key",
      source: "gitleaks",
    },
    {
      id: "secret.airtable_api_key",
      title: "Airtable API Key",
      source: "gitleaks",
    },
    {
      id: "secret.airtable_personnal_access_token",
      title: "Airtable Personal Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.algolia_api_key",
      title: "Algolia API Key",
      source: "gitleaks",
    },
    {
      id: "secret.alibaba_access_key_id",
      title: "Alibaba Access Key ID",
      source: "gitleaks",
    },
    {
      id: "secret.alibaba_secret_key",
      title: "Alibaba Secret Key",
      source: "gitleaks",
    },
    {
      id: "secret.anthropic_admin_api_key",
      title: "Anthropic Admin API Key",
      source: "gitleaks",
    },
    {
      id: "secret.anthropic_api_key",
      title: "Anthropic API Key",
      source: "gitleaks",
    },
    {
      id: "secret.artifactory_api_key",
      title: "Artifactory API Key",
      source: "gitleaks",
    },
    {
      id: "secret.artifactory_reference_token",
      title: "Artifactory Reference Token",
      source: "gitleaks",
    },
    {
      id: "secret.asana_client_id",
      title: "Asana Client ID",
      source: "gitleaks",
    },
    {
      id: "secret.asana_client_secret",
      title: "Asana Client Secret",
      source: "gitleaks",
    },
    {
      id: "secret.atlassian_api_token",
      title: "Atlassian API Token",
      source: "gitleaks",
    },
    {
      id: "secret.authress_service_client_access_key",
      title: "Authress Service Client Access Key",
      source: "gitleaks",
    },
    {
      id: "secret.aws_access_token",
      title: "AWS Credentials",
      source: "gitleaks",
    },
    {
      id: "secret.aws_amazon_bedrock_api_key_long_lived",
      title: "Amazon Bedrock API Key (Long-Lived)",
      source: "gitleaks",
    },
    {
      id: "secret.aws_amazon_bedrock_api_key_short_lived",
      title: "Amazon Bedrock API Key (Short-Lived)",
      source: "gitleaks",
    },
    {
      id: "secret.azure_ad_client_secret",
      title: "Azure AD Client Secret",
      source: "gitleaks",
    },
    {
      id: "secret.beamer_api_token",
      title: "Beamer API Token",
      source: "gitleaks",
    },
    {
      id: "secret.bitbucket_client_id",
      title: "Bitbucket Client ID",
      source: "gitleaks",
    },
    {
      id: "secret.bitbucket_client_secret",
      title: "Bitbucket Client Secret",
      source: "gitleaks",
    },
    {
      id: "secret.bittrex_access_key",
      title: "Bittrex Access Key",
      source: "gitleaks",
    },
    {
      id: "secret.bittrex_secret_key",
      title: "Bittrex Secret Key",
      source: "gitleaks",
    },
    {
      id: "secret.cisco_meraki_api_key",
      title: "Cisco Meraki API Key",
      source: "gitleaks",
    },
    {
      id: "secret.clickhouse_cloud_api_secret_key",
      title: "ClickHouse Cloud API Key",
      source: "gitleaks",
    },
    {
      id: "secret.clojars_api_token",
      title: "Clojars API Token",
      source: "gitleaks",
    },
    {
      id: "secret.cloudflare_api_key",
      title: "Cloudflare API Key",
      source: "gitleaks",
    },
    {
      id: "secret.cloudflare_global_api_key",
      title: "Cloudflare Global API Key",
      source: "gitleaks",
    },
    {
      id: "secret.cloudflare_origin_ca_key",
      title: "Cloudflare Origin Ca Key",
      source: "gitleaks",
    },
    {
      id: "secret.codecov_access_token",
      title: "Codecov Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.cohere_api_token",
      title: "Cohere API Token",
      source: "gitleaks",
    },
    {
      id: "secret.coinbase_access_token",
      title: "Coinbase Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.confluent_access_token",
      title: "Confluent Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.confluent_secret_key",
      title: "Confluent Secret Key",
      source: "gitleaks",
    },
    {
      id: "secret.contentful_delivery_api_token",
      title: "Contentful Delivery API Token",
      source: "gitleaks",
    },
    {
      id: "secret.curl_auth_header",
      title: "curl Authorization Header",
      source: "gitleaks",
    },
    {
      id: "secret.curl_auth_user",
      title: "curl Basic Auth",
      source: "gitleaks",
    },
    {
      id: "secret.databricks_api_token",
      title: "Databricks API Token",
      source: "gitleaks",
    },
    {
      id: "secret.datadog_access_token",
      title: "Datadog Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.defined_networking_api_token",
      title: "Defined Networking API Token",
      source: "gitleaks",
    },
    {
      id: "secret.digitalocean_access_token",
      title: "Digitalocean Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.digitalocean_pat",
      title: "Digitalocean PAT",
      source: "gitleaks",
    },
    {
      id: "secret.digitalocean_refresh_token",
      title: "Digitalocean Refresh Token",
      source: "gitleaks",
    },
    {
      id: "secret.discord_api_token",
      title: "Discord API Token",
      source: "gitleaks",
    },
    {
      id: "secret.discord_client_id",
      title: "Discord Client ID",
      source: "gitleaks",
    },
    {
      id: "secret.discord_client_secret",
      title: "Discord Client Secret",
      source: "gitleaks",
    },
    {
      id: "secret.doppler_api_token",
      title: "Doppler API Token",
      source: "gitleaks",
    },
    {
      id: "secret.droneci_access_token",
      title: "Droneci Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.dropbox_api_token",
      title: "Dropbox API Token",
      source: "gitleaks",
    },
    {
      id: "secret.dropbox_long_lived_api_token",
      title: "Dropbox Long Lived API Token",
      source: "gitleaks",
    },
    {
      id: "secret.dropbox_short_lived_api_token",
      title: "Dropbox Short Lived API Token",
      source: "gitleaks",
    },
    {
      id: "secret.duffel_api_token",
      title: "Duffel API Token",
      source: "gitleaks",
    },
    {
      id: "secret.dynatrace_api_token",
      title: "Dynatrace API Token",
      source: "gitleaks",
    },
    {
      id: "secret.easypost_api_token",
      title: "Easypost API Token",
      source: "gitleaks",
    },
    {
      id: "secret.easypost_test_api_token",
      title: "Easypost Test API Token",
      source: "gitleaks",
    },
    {
      id: "secret.etsy_access_token",
      title: "Etsy Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.facebook_access_token",
      title: "Facebook Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.facebook_page_access_token",
      title: "Facebook Page Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.facebook_secret",
      title: "Facebook Secret",
      source: "gitleaks",
    },
    {
      id: "secret.fastly_api_token",
      title: "Fastly API Token",
      source: "gitleaks",
    },
    {
      id: "secret.finicity_api_token",
      title: "Finicity API Token",
      source: "gitleaks",
    },
    {
      id: "secret.finicity_client_secret",
      title: "Finicity Client Secret",
      source: "gitleaks",
    },
    {
      id: "secret.finnhub_access_token",
      title: "Finnhub Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.flickr_access_token",
      title: "Flickr Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.flutterwave_encryption_key",
      title: "Flutterwave Encryption Key",
      source: "gitleaks",
    },
    {
      id: "secret.flutterwave_public_key",
      title: "Flutterwave Public Key",
      source: "gitleaks",
    },
    {
      id: "secret.flutterwave_secret_key",
      title: "Flutterwave Secret Key",
      source: "gitleaks",
    },
    {
      id: "secret.flyio_access_token",
      title: "Fly.io Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.frameio_api_token",
      title: "Frameio API Token",
      source: "gitleaks",
    },
    {
      id: "secret.freemius_secret_key",
      title: "Freemius Secret Key",
      source: "gitleaks",
    },
    {
      id: "secret.freshbooks_access_token",
      title: "Freshbooks Access Token",
      source: "gitleaks",
    },
    { id: "secret.gcp_api_key", title: "GCP API Key", source: "gitleaks" },
    {
      id: "secret.generic_api_key",
      title: "Generic API Key",
      source: "gitleaks",
    },
    {
      id: "secret.github_app_token",
      title: "GitHub App Token",
      source: "gitleaks",
    },
    {
      id: "secret.github_fine_grained_pat",
      title: "GitHub Fine-Grained PAT",
      source: "gitleaks",
    },
    {
      id: "secret.github_oauth",
      title: "GitHub OAuth Token",
      source: "gitleaks",
    },
    {
      id: "secret.github_pat",
      title: "GitHub Personal Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.github_refresh_token",
      title: "GitHub Refresh Token",
      source: "gitleaks",
    },
    {
      id: "secret.gitlab_cicd_job_token",
      title: "GitLab CI/CD Job Token",
      source: "gitleaks",
    },
    {
      id: "secret.gitlab_deploy_token",
      title: "GitLab Deploy Token",
      source: "gitleaks",
    },
    {
      id: "secret.gitlab_feature_flag_client_token",
      title: "GitLab Feature Flag Token",
      source: "gitleaks",
    },
    {
      id: "secret.gitlab_feed_token",
      title: "GitLab Feed Token",
      source: "gitleaks",
    },
    {
      id: "secret.gitlab_incoming_mail_token",
      title: "GitLab Incoming Mail Token",
      source: "gitleaks",
    },
    {
      id: "secret.gitlab_kubernetes_agent_token",
      title: "GitLab K8s Agent Token",
      source: "gitleaks",
    },
    {
      id: "secret.gitlab_oauth_app_secret",
      title: "GitLab OAuth App Secret",
      source: "gitleaks",
    },
    {
      id: "secret.gitlab_pat",
      title: "GitLab Personal Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.gitlab_pat_routable",
      title: "GitLab PAT (Routable)",
      source: "gitleaks",
    },
    {
      id: "secret.gitlab_ptt",
      title: "GitLab Pipeline Trigger Token",
      source: "gitleaks",
    },
    {
      id: "secret.gitlab_rrt",
      title: "GitLab Runner Registration Token",
      source: "gitleaks",
    },
    {
      id: "secret.gitlab_runner_authentication_token",
      title: "GitLab Runner Auth Token",
      source: "gitleaks",
    },
    {
      id: "secret.gitlab_runner_authentication_token_routable",
      title: "GitLab Runner Auth Token (Routable)",
      source: "gitleaks",
    },
    {
      id: "secret.gitlab_scim_token",
      title: "GitLab SCIM Token",
      source: "gitleaks",
    },
    {
      id: "secret.gitlab_session_cookie",
      title: "GitLab Session Cookie",
      source: "gitleaks",
    },
    {
      id: "secret.gitter_access_token",
      title: "Gitter Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.gocardless_api_token",
      title: "Gocardless API Token",
      source: "gitleaks",
    },
    {
      id: "secret.grafana_api_key",
      title: "Grafana API Key",
      source: "gitleaks",
    },
    {
      id: "secret.grafana_cloud_api_token",
      title: "Grafana Cloud API Token",
      source: "gitleaks",
    },
    {
      id: "secret.grafana_service_account_token",
      title: "Grafana Service Account Token",
      source: "gitleaks",
    },
    {
      id: "secret.harness_api_key",
      title: "Harness API Key",
      source: "gitleaks",
    },
    {
      id: "secret.hashicorp_tf_api_token",
      title: "HashiCorp Terraform API Token",
      source: "gitleaks",
    },
    {
      id: "secret.hashicorp_tf_password",
      title: "HashiCorp Terraform Password",
      source: "gitleaks",
    },
    {
      id: "secret.heroku_api_key",
      title: "Heroku API Key",
      source: "gitleaks",
    },
    {
      id: "secret.heroku_api_key_v2",
      title: "Heroku API Key (v2)",
      source: "gitleaks",
    },
    {
      id: "secret.hubspot_api_key",
      title: "Hubspot API Key",
      source: "gitleaks",
    },
    {
      id: "secret.huggingface_access_token",
      title: "Hugging Face Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.huggingface_organization_api_token",
      title: "Hugging Face Org API Token",
      source: "gitleaks",
    },
    {
      id: "secret.infracost_api_token",
      title: "Infracost API Token",
      source: "gitleaks",
    },
    {
      id: "secret.intercom_api_key",
      title: "Intercom API Key",
      source: "gitleaks",
    },
    {
      id: "secret.intra42_client_secret",
      title: "Intra42 Client Secret",
      source: "gitleaks",
    },
    { id: "secret.jfrog_api_key", title: "Jfrog API Key", source: "gitleaks" },
    {
      id: "secret.jfrog_identity_token",
      title: "Jfrog Identity Token",
      source: "gitleaks",
    },
    { id: "secret.jwt", title: "JSON Web Token (JWT)", source: "gitleaks" },
    {
      id: "secret.jwt_base64",
      title: "JSON Web Token (Base64)",
      source: "gitleaks",
    },
    {
      id: "secret.kraken_access_token",
      title: "Kraken Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.kubernetes_secret_yaml",
      title: "Kubernetes Secret (YAML)",
      source: "gitleaks",
    },
    {
      id: "secret.kucoin_access_token",
      title: "Kucoin Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.kucoin_secret_key",
      title: "Kucoin Secret Key",
      source: "gitleaks",
    },
    {
      id: "secret.launchdarkly_access_token",
      title: "Launchdarkly Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.linear_api_key",
      title: "Linear API Key",
      source: "gitleaks",
    },
    {
      id: "secret.linear_client_secret",
      title: "Linear Client Secret",
      source: "gitleaks",
    },
    {
      id: "secret.linkedin_client_id",
      title: "Linkedin Client ID",
      source: "gitleaks",
    },
    {
      id: "secret.linkedin_client_secret",
      title: "Linkedin Client Secret",
      source: "gitleaks",
    },
    { id: "secret.lob_api_key", title: "Lob API Key", source: "gitleaks" },
    {
      id: "secret.lob_pub_api_key",
      title: "Lob Pub API Key",
      source: "gitleaks",
    },
    {
      id: "secret.looker_client_id",
      title: "Looker Client ID",
      source: "gitleaks",
    },
    {
      id: "secret.looker_client_secret",
      title: "Looker Client Secret",
      source: "gitleaks",
    },
    {
      id: "secret.mailchimp_api_key",
      title: "Mailchimp API Key",
      source: "gitleaks",
    },
    {
      id: "secret.mailgun_private_api_token",
      title: "Mailgun Private API Token",
      source: "gitleaks",
    },
    {
      id: "secret.mailgun_pub_key",
      title: "Mailgun Pub Key",
      source: "gitleaks",
    },
    {
      id: "secret.mailgun_signing_key",
      title: "Mailgun Signing Key",
      source: "gitleaks",
    },
    {
      id: "secret.mapbox_api_token",
      title: "Mapbox API Token",
      source: "gitleaks",
    },
    {
      id: "secret.mattermost_access_token",
      title: "Mattermost Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.maxmind_license_key",
      title: "Maxmind License Key",
      source: "gitleaks",
    },
    {
      id: "secret.messagebird_api_token",
      title: "Messagebird API Token",
      source: "gitleaks",
    },
    {
      id: "secret.messagebird_client_id",
      title: "Messagebird Client ID",
      source: "gitleaks",
    },
    {
      id: "secret.microsoft_teams_webhook",
      title: "Microsoft Teams Webhook",
      source: "gitleaks",
    },
    {
      id: "secret.netlify_access_token",
      title: "Netlify Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.new_relic_browser_api_token",
      title: "New Relic Browser API Token",
      source: "gitleaks",
    },
    {
      id: "secret.new_relic_insert_key",
      title: "New Relic Insert Key",
      source: "gitleaks",
    },
    {
      id: "secret.new_relic_user_api_id",
      title: "New Relic User API ID",
      source: "gitleaks",
    },
    {
      id: "secret.new_relic_user_api_key",
      title: "New Relic User API Key",
      source: "gitleaks",
    },
    {
      id: "secret.notion_api_token",
      title: "Notion API Token",
      source: "gitleaks",
    },
    {
      id: "secret.npm_access_token",
      title: "npm Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.nuget_config_password",
      title: "NuGet Config Password",
      source: "gitleaks",
    },
    {
      id: "secret.nytimes_access_token",
      title: "NY Times Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.octopus_deploy_api_key",
      title: "Octopus Deploy API Key",
      source: "gitleaks",
    },
    {
      id: "secret.okta_access_token",
      title: "Okta Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.openai_api_key",
      title: "OpenAI API Key",
      source: "gitleaks",
    },
    {
      id: "secret.openshift_user_token",
      title: "Openshift User Token",
      source: "gitleaks",
    },
    {
      id: "secret.perplexity_api_key",
      title: "Perplexity API Key",
      source: "gitleaks",
    },
    { id: "secret.pkcs12_file", title: "PKCS #12 File", source: "gitleaks" },
    {
      id: "secret.plaid_api_token",
      title: "Plaid API Token",
      source: "gitleaks",
    },
    {
      id: "secret.plaid_client_id",
      title: "Plaid Client ID",
      source: "gitleaks",
    },
    {
      id: "secret.plaid_secret_key",
      title: "Plaid Secret Key",
      source: "gitleaks",
    },
    {
      id: "secret.planetscale_api_token",
      title: "Planetscale API Token",
      source: "gitleaks",
    },
    {
      id: "secret.planetscale_oauth_token",
      title: "Planetscale OAUTH Token",
      source: "gitleaks",
    },
    {
      id: "secret.planetscale_password",
      title: "Planetscale Password",
      source: "gitleaks",
    },
    {
      id: "secret.postman_api_token",
      title: "Postman API Token",
      source: "gitleaks",
    },
    {
      id: "secret.prefect_api_token",
      title: "Prefect API Token",
      source: "gitleaks",
    },
    {
      id: "secret.private_key",
      title: "Private Key (RSA/EC/DSA/SSH)",
      source: "gitleaks",
    },
    {
      id: "secret.privateai_api_token",
      title: "Privateai API Token",
      source: "gitleaks",
    },
    {
      id: "secret.pulumi_api_token",
      title: "Pulumi API Token",
      source: "gitleaks",
    },
    {
      id: "secret.pypi_upload_token",
      title: "PyPI Upload Token",
      source: "gitleaks",
    },
    {
      id: "secret.rapidapi_access_token",
      title: "Rapidapi Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.readme_api_token",
      title: "Readme API Token",
      source: "gitleaks",
    },
    {
      id: "secret.rubygems_api_token",
      title: "Rubygems API Token",
      source: "gitleaks",
    },
    {
      id: "secret.scalingo_api_token",
      title: "Scalingo API Token",
      source: "gitleaks",
    },
    {
      id: "secret.sendbird_access_id",
      title: "Sendbird Access ID",
      source: "gitleaks",
    },
    {
      id: "secret.sendbird_access_token",
      title: "Sendbird Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.sendgrid_api_token",
      title: "Sendgrid API Token",
      source: "gitleaks",
    },
    {
      id: "secret.sendinblue_api_token",
      title: "Sendinblue API Token",
      source: "gitleaks",
    },
    {
      id: "secret.sentry_access_token",
      title: "Sentry Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.sentry_org_token",
      title: "Sentry Org Token",
      source: "gitleaks",
    },
    {
      id: "secret.sentry_user_token",
      title: "Sentry User Token",
      source: "gitleaks",
    },
    {
      id: "secret.settlemint_application_access_token",
      title: "SettleMint Application Token",
      source: "gitleaks",
    },
    {
      id: "secret.settlemint_personal_access_token",
      title: "SettleMint Personal Token",
      source: "gitleaks",
    },
    {
      id: "secret.settlemint_service_access_token",
      title: "SettleMint Service Token",
      source: "gitleaks",
    },
    {
      id: "secret.shippo_api_token",
      title: "Shippo API Token",
      source: "gitleaks",
    },
    {
      id: "secret.shopify_access_token",
      title: "Shopify Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.shopify_custom_access_token",
      title: "Shopify Custom Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.shopify_private_app_access_token",
      title: "Shopify Private App Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.shopify_shared_secret",
      title: "Shopify Shared Secret",
      source: "gitleaks",
    },
    {
      id: "secret.sidekiq_secret",
      title: "Sidekiq Secret",
      source: "gitleaks",
    },
    {
      id: "secret.sidekiq_sensitive_url",
      title: "Sidekiq Sensitive URL",
      source: "gitleaks",
    },
    {
      id: "secret.slack_app_token",
      title: "Slack App Token",
      source: "gitleaks",
    },
    {
      id: "secret.slack_bot_token",
      title: "Slack Bot Token",
      source: "gitleaks",
    },
    {
      id: "secret.slack_config_access_token",
      title: "Slack Config Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.slack_config_refresh_token",
      title: "Slack Config Refresh Token",
      source: "gitleaks",
    },
    {
      id: "secret.slack_legacy_bot_token",
      title: "Slack Legacy Bot Token",
      source: "gitleaks",
    },
    {
      id: "secret.slack_legacy_token",
      title: "Slack Legacy Token",
      source: "gitleaks",
    },
    {
      id: "secret.slack_legacy_workspace_token",
      title: "Slack Legacy Workspace Token",
      source: "gitleaks",
    },
    {
      id: "secret.slack_user_token",
      title: "Slack User Token",
      source: "gitleaks",
    },
    {
      id: "secret.slack_webhook_url",
      title: "Slack Webhook URL",
      source: "gitleaks",
    },
    {
      id: "secret.snyk_api_token",
      title: "Snyk API Token",
      source: "gitleaks",
    },
    {
      id: "secret.sonar_api_token",
      title: "Sonar API Token",
      source: "gitleaks",
    },
    {
      id: "secret.sourcegraph_access_token",
      title: "Sourcegraph Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.square_access_token",
      title: "Square Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.squarespace_access_token",
      title: "Squarespace Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.stripe_access_token",
      title: "Stripe Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.sumologic_access_id",
      title: "Sumologic Access ID",
      source: "gitleaks",
    },
    {
      id: "secret.sumologic_access_token",
      title: "Sumologic Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.telegram_bot_api_token",
      title: "Telegram Bot API Token",
      source: "gitleaks",
    },
    {
      id: "secret.travisci_access_token",
      title: "Travisci Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.twilio_api_key",
      title: "Twilio API Key",
      source: "gitleaks",
    },
    {
      id: "secret.twitch_api_token",
      title: "Twitch API Token",
      source: "gitleaks",
    },
    {
      id: "secret.twitter_access_secret",
      title: "Twitter Access Secret",
      source: "gitleaks",
    },
    {
      id: "secret.twitter_access_token",
      title: "Twitter Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.twitter_api_key",
      title: "Twitter API Key",
      source: "gitleaks",
    },
    {
      id: "secret.twitter_api_secret",
      title: "Twitter API Secret",
      source: "gitleaks",
    },
    {
      id: "secret.twitter_bearer_token",
      title: "Twitter Bearer Token",
      source: "gitleaks",
    },
    {
      id: "secret.typeform_api_token",
      title: "Typeform API Token",
      source: "gitleaks",
    },
    {
      id: "secret.vault_batch_token",
      title: "Vault Batch Token",
      source: "gitleaks",
    },
    {
      id: "secret.vault_service_token",
      title: "Vault Service Token",
      source: "gitleaks",
    },
    {
      id: "secret.yandex_access_token",
      title: "Yandex Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.yandex_api_key",
      title: "Yandex API Key",
      source: "gitleaks",
    },
    {
      id: "secret.yandex_aws_access_token",
      title: "Yandex Aws Access Token",
      source: "gitleaks",
    },
    {
      id: "secret.zendesk_secret_key",
      title: "Zendesk Secret Key",
      source: "gitleaks",
    },
  ],
  financial: [
    {
      id: "pii.credit_card",
      title: "Credit card number (12-19 digits)",
      source: "presidio",
    },
    {
      id: "pii.iban_code",
      title: "International Bank Account Number",
      source: "presidio",
    },
    {
      id: "pii.us_bank_number",
      title: "US bank account number (8-17 digits)",
      source: "presidio",
    },
    { id: "pii.crypto", title: "Bitcoin wallet address", source: "presidio" },
  ],
  pii: [
    // PERSON intentionally omitted: Presidio's NER-backed person detection
    // is too brittle for the runtime hot path (false positives on common
    // capitalized words). Re-enable once we have a confidence threshold or
    // a scoped allow-list.
    { id: "pii.email_address", title: "Email address", source: "presidio" },
    { id: "pii.phone_number", title: "Telephone number", source: "presidio" },
    {
      id: "pii.ip_address",
      title: "IPv4 or IPv6 address",
      source: "presidio",
    },
    {
      id: "pii.mac_address",
      title: "Network interface identifier",
      source: "presidio",
    },
  ],
  government_ids: [
    {
      id: "pii.us_ssn",
      title: "US Social Security Number",
      source: "presidio",
    },
    {
      id: "pii.us_passport",
      title: "US passport number (9 digits)",
      source: "presidio",
    },
    {
      // Kept for legacy risk_results display; hidden from the policy form.
      // Upstream Presidio regex is unusably broad — see microsoft/presidio#1063
      // and PR #3172. Server-side blacklist also drops it before any
      // Presidio request.
      id: "pii.us_driver_license",
      title: "US state-issued driver license",
      source: "presidio",
      hidden: true,
    },
    {
      id: "pii.us_itin",
      title: "US Individual Taxpayer ID Number",
      source: "presidio",
    },
    {
      id: "pii.uk_nhs",
      title: "UK National Health Service number",
      source: "presidio",
    },
    {
      id: "pii.uk_nino",
      title: "UK National Insurance Number",
      source: "presidio",
    },
    {
      id: "pii.uk_passport",
      title: "UK passport number",
      source: "presidio",
    },
    {
      id: "pii.es_nif",
      title: "Spain personal tax ID (NIF)",
      source: "presidio",
    },
    {
      id: "pii.it_fiscal_code",
      title: "Italy personal identification code",
      source: "presidio",
    },
    {
      id: "pii.au_tfn",
      title: "Australia Tax File Number",
      source: "presidio",
    },
    {
      id: "pii.in_pan",
      title: "India Permanent Account Number",
      source: "presidio",
    },
    {
      id: "pii.in_aadhaar",
      title: "India Aadhaar (12-digit identity)",
      source: "presidio",
    },
    {
      id: "pii.sg_nric_fin",
      title: "Singapore NRIC / FIN",
      source: "presidio",
    },
  ],
  healthcare: [
    {
      id: "pii.medical_license",
      title: "Common medical license numbers",
      source: "presidio",
    },
    {
      id: "pii.us_mbi",
      title: "US Medicare Beneficiary Identifier",
      source: "presidio",
    },
    {
      id: "pii.us_npi",
      title: "US National Provider Identifier",
      source: "presidio",
    },
    {
      id: "pii.medical_disease_disorder",
      title: "Disease or disorder mention",
      source: "presidio",
    },
    {
      id: "pii.medical_medication",
      title: "Medication or drug name",
      source: "presidio",
    },
    {
      id: "pii.medical_therapeutic_procedure",
      title: "Treatment or diagnostic procedure",
      source: "presidio",
    },
    {
      id: "pii.medical_clinical_event",
      title: "Clinical event mention",
      source: "presidio",
    },
    {
      id: "pii.medical_biological_attribute",
      title: "Biological attribute or measurement",
      source: "presidio",
    },
    {
      id: "pii.medical_family_history",
      title: "Family medical history reference",
      source: "presidio",
    },
  ],
  prompt_policy: [
    {
      id: "llm_judge",
      title: "LLM Judge",
      source: "llm_judge",
      hidden: true,
    },
  ],
  // prompt_injection is enabled at the category level; the judge is selected
  // by category, not by individual rule.
  prompt_injection: [],
  off_policy: [
    {
      id: "pii.harmful_content_request",
      title: "Request to generate harmful or dangerous content",
      source: "presidio",
    },
    {
      id: "pii.policy_violation",
      title: "Request that violates acceptable use policy",
      source: "presidio",
    },
    {
      id: "pii.unauthorized_action",
      title: "Attempt to perform unauthorized actions",
      source: "presidio",
    },
    {
      id: "pii.topic_boundary_violation",
      title: "Request outside permitted topic boundaries",
      source: "presidio",
    },
  ],
  shadow_mcp: [],
  destructive_tool: [],
  cli_destructive: [],
  account_identity: [
    {
      id: "identity.personal_account",
      title: "Personal AI account",
      description: `When on, flags any session signed in with a personal AI account (e.g. an individual Claude plan) instead of your organization's team account. Needs no configuration. ${PERSONAL_ACCOUNT_GOVERNANCE_NOTE}`,
      source: "account_identity",
    },
    {
      id: "identity.unapproved_domain",
      title: "Unapproved email domain",
      description:
        "When on, flags any session whose AI-account email domain is not in the approved email domains list. Does nothing until at least one approved domain is added.",
      source: "account_identity",
    },
  ],
  custom: [],
};
